import { useEffect, useRef } from 'react';
import type { JSX } from 'react';
import { prefersReducedMotion } from '../../theme/motion';
import { readToken, withAlpha } from '../../theme/tokens';
import { TAU, fitCanvas, measureCanvas, observeCanvasResize } from './canvas';
import type { CanvasBox } from './canvas';
import { cx } from './cx';

/**
 * The glowing globe on the key-entry gate: a lit sphere inside three
 * intersecting orbital rings, with data nodes travelling along them.
 *
 * A port of `GlobeHero` in `ui/components/GlobeHero.kt`, drawn on a `<canvas>`
 * for the same reason it is a Compose `Canvas` there: it has to stay crisp from
 * a 320px screen to a landscape iPad, and a raster would need several densities
 * to manage that.
 *
 * This is the only continuously-running animation in the app, and it exists only
 * on this screen. It is unmounted the moment a key is accepted, so nothing here
 * runs while a check cycle does — which matters more here than on Android,
 * because a WebClip only measures while the screen is on and every frame it
 * draws is drawn out of the same battery the run depends on (SPEC §9).
 *
 * **The screen is not always brief.** A device whose key has been revoked parks
 * on this gate until somebody physically re-provisions it, which can be hours on
 * a desk with the screen awake. That is why everything below that does not
 * change between frames — the palette, the eleven `rgba()` strings, the
 * measured box, the two gradients — is resolved once and held, and the frame
 * loop is left with nothing to do but the drawing itself.
 */
export function GlobeHero({ className }: { className?: string }): JSX.Element {
  const canvas = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const element = canvas.current;
    if (!element) return;
    const context = element.getContext('2d');
    // A gate that cannot draw its decoration still has to accept a device key.
    if (!context) return;

    // Read once on mount, matching Android's `rememberAnimationsEnabled`, which
    // is a `remember(context)` and likewise does not re-read while the screen is
    // up. Live-observing the media query would be a divergence from the
    // reference for a preference nobody changes with a device key half typed.
    const still = prefersReducedMotion();

    // Read once for the same reason, and with more at stake: `readPalette` is
    // three `getComputedStyle` reads, each of which forces the browser to
    // resolve style. Called from the frame loop that was 180 style resolutions a
    // second for three values that cannot change while the gate is up.
    const palette = readPalette();

    // The measured box and the gradients derived from it change only when the
    // element does, so they are rebuilt on resize rather than per frame. Reading
    // `clientWidth` inside a rAF callback is a layout read per frame for a box
    // that `.globe-hero` pins to `width: 100%; max-width: 260px; aspect-ratio: 1`.
    let scene = buildScene(element, context, palette);
    const remeasure = (): void => {
      scene = buildScene(element, context, palette);
    };

    if (still) {
      // Deliberately not "create the loop and set its duration to zero": a
      // requestAnimationFrame loop still schedules a callback per frame and
      // still repaints, which is precisely the cost Motion.kt exists to avoid.
      // When motion is off no loop is created at all and the canvas draws a
      // single static composition.
      const render = (): void => {
        if (scene) paint(element, context, scene, palette, STATIC_ANGLE);
      };
      render();
      return observeCanvasResize(element, () => {
        remeasure();
        render();
      });
    }

    let frame = 0;
    let origin = 0;
    const step = (now: number) => {
      // Seeded from the first callback rather than from mount, so the ring never
      // starts part-way round after a slow first paint.
      if (origin === 0) origin = now;
      const angle = (((now - origin) / ORBIT_PERIOD_MS) * 360) % 360;
      if (scene) paint(element, context, scene, palette, angle);
      frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);

    // The animated branch observes too. It used to be able to skip this because
    // it re-measured on every frame; now that it does not, the observer is the
    // only thing that notices a rotation or a keyboard resizing the box — and it
    // is also what recovers a first frame that landed before layout gave the
    // element a size.
    const stopObserving = observeCanvasResize(element, remeasure);

    // Cancelling matters: React unmounts this the instant a key is accepted, and
    // an orphaned rAF chain keeps repainting a detached canvas for as long as
    // the WebClip stays open.
    return () => {
      window.cancelAnimationFrame(frame);
      stopObserving();
    };
  }, []);

  return (
    // No accessible name: this is decoration, and it carries no information that
    // is not already in the app title beneath it. Announcing "glowing globe"
    // would just put noise between a screen-reader user and the key field.
    <canvas className={cx('globe-hero', className)} ref={canvas} aria-hidden="true" />
  );
}

/** One frame of the whole composition, at the given orbital angle in degrees. */
function paint(
  canvas: HTMLCanvasElement,
  context: CanvasRenderingContext2D,
  scene: Scene,
  palette: Palette,
  rotation: number,
): void {
  // Re-fitting with the cached box costs no layout read, and `fitCanvas` skips
  // the buffer reallocation when nothing has changed — so per frame this is the
  // `clearRect` and the transform, which is all a frame actually needs.
  fitCanvas(canvas, context, scene);

  drawBloom(context, scene);
  drawSphere(context, scene, palette);
  drawMeridians(context, scene, palette);
  for (const orbit of palette.orbits) drawOrbit(context, scene, orbit, rotation, palette);
}

/**
 * Everything about the drawing that depends on its size and not on the clock.
 *
 * `null` when the element has no size yet; the caller draws nothing and waits
 * for the resize observer, exactly as it did when every frame measured for
 * itself.
 */
function buildScene(
  canvas: HTMLCanvasElement,
  context: CanvasRenderingContext2D,
  palette: Palette,
): Scene | null {
  const box = measureCanvas(canvas);
  if (!box) return null;

  const centreX = box.width / 2;
  const centreY = box.height / 2;
  const radius = Math.min(box.width, box.height) * GLOBE_RADIUS_FRACTION;
  const bloomRadius = radius * BLOOM_SCALE;

  const bloom = context.createRadialGradient(centreX, centreY, 0, centreX, centreY, bloomRadius);
  bloom.addColorStop(0, palette.bloomInner);
  // Cyan at zero alpha, not `transparent`. A canvas gradient interpolates in
  // non-premultiplied RGBA, so fading to `transparent` (which is transparent
  // *black*) drags the ramp through a dirty navy on the way out. Compose's
  // `NeonCyan.copy(alpha = 0f)` is this, and the difference is visible.
  bloom.addColorStop(1, palette.bloomFade);

  // The light source sits up and to the left, which is what turns a flat disc
  // into something that reads as a ball.
  const litX = centreX - radius * 0.35;
  const litY = centreY - radius * 0.4;
  const sphere = context.createRadialGradient(litX, litY, 0, litX, litY, radius * 1.6);
  // Three stops at 0 / 0.5 / 1: Compose's `Brush.radialGradient(colors)` with no
  // explicit stop list spaces them evenly, and the midpoint is what keeps the
  // electric blue a band rather than a hairline between cyan and navy.
  sphere.addColorStop(0, palette.sphereCore);
  sphere.addColorStop(0.5, palette.sphereMid);
  sphere.addColorStop(1, palette.sphereEdge);

  return { ...box, centreX, centreY, radius, bloomRadius, bloom, sphere };
}

function drawBloom(context: CanvasRenderingContext2D, scene: Scene): void {
  context.fillStyle = scene.bloom;
  context.beginPath();
  context.arc(scene.centreX, scene.centreY, scene.bloomRadius, 0, TAU);
  context.fill();
}

function drawSphere(context: CanvasRenderingContext2D, scene: Scene, palette: Palette): void {
  context.fillStyle = scene.sphere;
  context.beginPath();
  context.arc(scene.centreX, scene.centreY, scene.radius, 0, TAU);
  context.fill();

  // Rim light: brightest where the sphere turns away from the viewer.
  context.strokeStyle = palette.rim;
  context.lineWidth = RIM_WIDTH;
  context.beginPath();
  context.arc(scene.centreX, scene.centreY, scene.radius, 0, TAU);
  context.stroke();
}

/** Latitude and longitude lines, flattened into ellipses by the projection. */
function drawMeridians(context: CanvasRenderingContext2D, scene: Scene, palette: Palette): void {
  context.strokeStyle = palette.grid;
  context.lineWidth = GRID_WIDTH;

  for (const fraction of LATITUDES) {
    context.beginPath();
    context.ellipse(scene.centreX, scene.centreY, scene.radius, scene.radius * fraction, 0, 0, TAU);
    context.stroke();
  }
  for (const fraction of LONGITUDES) {
    context.beginPath();
    context.ellipse(scene.centreX, scene.centreY, scene.radius * fraction, scene.radius, 0, 0, TAU);
    context.stroke();
  }
}

/**
 * One orbital ring plus the node riding it.
 *
 * The ring is a tilted ellipse; the node is placed by walking the same ellipse
 * parametrically inside the same rotated frame, so it always sits exactly on the
 * line no matter the tilt.
 */
function drawOrbit(
  context: CanvasRenderingContext2D,
  scene: Scene,
  orbit: Orbit,
  rotation: number,
  palette: Palette,
): void {
  const ring = orbit.ring;
  const semiMajor = scene.radius * ring.scale;
  const semiMinor = semiMajor * ring.flatten;

  context.save();
  // The canvas equivalent of Compose's `rotate(degrees, pivot = centre)`: move
  // the origin to the centre, turn, and draw around (0, 0).
  context.translate(scene.centreX, scene.centreY);
  context.rotate((ring.tilt * Math.PI) / 180);

  context.strokeStyle = orbit.stroke;
  context.lineWidth = ORBIT_WIDTH;
  context.beginPath();
  context.ellipse(0, 0, semiMajor, semiMinor, 0, 0, TAU);
  context.stroke();

  const theta = ((rotation * ring.speed + ring.phase) * Math.PI) / 180;
  const nodeX = semiMajor * Math.cos(theta);
  const nodeY = semiMinor * Math.sin(theta);
  context.fillStyle = palette.nodeGlow;
  context.beginPath();
  context.arc(nodeX, nodeY, NODE_GLOW, 0, TAU);
  context.fill();
  context.fillStyle = palette.nodeCore;
  context.beginPath();
  context.arc(nodeX, nodeY, NODE_CORE, 0, TAU);
  context.fill();

  context.restore();
}

/** The size-dependent half of a frame: the box, the geometry, the gradients. */
interface Scene extends CanvasBox {
  centreX: number;
  centreY: number;
  radius: number;
  bloomRadius: number;
  bloom: CanvasGradient;
  sphere: CanvasGradient;
}

/** A ring and the colour it is stroked in — paired so neither can be indexed apart. */
interface Orbit {
  ring: Ring;
  stroke: string;
}

/**
 * Every colour string the drawing paints with, finished and ready to assign.
 *
 * Not the three raw tokens: the eleven values below are what the frame actually
 * needs, and every alpha in them is a constant — including `ring.alpha`, which
 * comes from the frozen `RINGS`. Deriving them per frame meant two regexes, a
 * `parseInt` and a template build, eleven times a frame, to rebuild eleven
 * strings that had not changed since the screen opened.
 */
interface Palette {
  bloomInner: string;
  bloomFade: string;
  sphereCore: string;
  sphereMid: string;
  sphereEdge: string;
  rim: string;
  grid: string;
  nodeGlow: string;
  nodeCore: string;
  orbits: readonly Orbit[];
}

/**
 * Resolves the three tokens this drawing uses and builds the strings from them.
 *
 * The colours are not repeated here as literals: a canvas cannot resolve
 * `var()`, and a hard-coded `#00f0ff` in a component is exactly the second
 * source of truth tokens.css warns about. `readToken` carries the fallbacks and
 * the reason each one is needed.
 */
function readPalette(): Palette {
  const cyan = readToken('--neon-cyan', '#00f0ff');
  const ctaStart = readToken('--cta-gradient-start', '#1e66ff');
  // Android names this `DarkPrimaryContainer`; tokens.css spells it
  // `--primary-container`. Same colour, same role.
  const primaryContainer = readToken('--primary-container', '#0c2a63');

  return {
    bloomInner: withAlpha(cyan, 0.18),
    bloomFade: withAlpha(cyan, 0),
    sphereCore: withAlpha(cyan, 0.55),
    sphereMid: ctaStart,
    sphereEdge: primaryContainer,
    rim: withAlpha(cyan, 0.55),
    grid: withAlpha(cyan, 0.22),
    nodeGlow: withAlpha(cyan, 0.25),
    nodeCore: cyan,
    orbits: RINGS.map((ring) => ({ ring, stroke: withAlpha(cyan, ring.alpha) })),
  };
}

interface Ring {
  scale: number;
  flatten: number;
  tilt: number;
  phase: number;
  speed: number;
  alpha: number;
}

/** `RINGS` from GlobeHero.kt, value for value. */
const RINGS: readonly Ring[] = [
  { scale: 1.55, flatten: 0.34, tilt: -18, phase: 0, speed: 1, alpha: 0.55 },
  { scale: 1.3, flatten: 0.3, tilt: 42, phase: 140, speed: -1.4, alpha: 0.4 },
  { scale: 1.75, flatten: 0.22, tilt: 78, phase: 250, speed: 0.7, alpha: 0.28 },
];

/** Slow on purpose: at anything faster this reads as a loading spinner. */
const ORBIT_PERIOD_MS = 20_000;

/** The frame the static composition lands on — chosen so no node sits behind the sphere. */
const STATIC_ANGLE = 24;

const GLOBE_RADIUS_FRACTION = 0.26;
const BLOOM_SCALE = 2.1;
const LATITUDES = [0.32, 0.66];
const LONGITUDES = [0.34, 0.7];

// Android's dp widths. 1dp and 1 CSS px are the same unit — both are 1/160 of a
// logical inch at baseline density — so they transfer with no conversion, and
// the device pixel ratio is applied once by `fitCanvas`.
const RIM_WIDTH = 1.5;
const GRID_WIDTH = 1;
const ORBIT_WIDTH = 1.5;
const NODE_CORE = 3;
const NODE_GLOW = 7;
